import React, { useCallback, useMemo, useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useIntl, defineMessages } from 'react-intl-next';

import { SpotlightTarget } from '@atlaskit/onboarding';
import { CustomTitle } from '@atlaskit/navigation-system/top-nav-items';
import { SideNavToggleButton } from '@atlaskit/navigation-system/layout/side-nav';
import { TopNavStart, TopNavMiddle, TopNavEnd } from '@atlaskit/navigation-system/layout/top-nav';

import { useSessionData } from '@confluence/session-data';
import { ErrorDisplay, isUnauthorizedError } from '@confluence/error-boundary';
import {
	BULK_ACTIONS_IN_CONTENT_TREE_EXPERIENCE,
	APP_NAV_GLOBAL_EXPERIENCE,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics';
import { APP_NAVIGATION_METRIC } from '@confluence/browser-metrics/entry-points/app-navigation.metric';
import {
	PerformanceEnd,
	PERFORMANCE_SUBJECT_navigation,
	PERFORMANCE_SUBJECT_navigationFMP,
} from '@confluence/performance';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { ShortcutVisualizer, UNIVERSAL_SIDE_NAVIGATION_SHORTCUT } from '@confluence/shortcuts';
import { EditionAwareness } from '@confluence/edition-awareness';
import { fg } from '@confluence/feature-gating';
import { EditionAwarenessUpsellExperiment } from '@confluence/experiment-edition-awareness-for-standard-to-premium';
import { useContentTreeSelection } from '@confluence/content-tree-actions';
import { markErrorAsHandled } from '@confluence/graphql';

import { TopNavigationQuery } from './TopNavigationQuery.graphql';
import type { TopNavigationQuery as QueryData } from './__types__/TopNavigationQuery';
import { AppSwitcherButton } from './AppSwitcherButton';
import { DeveloperButtons } from './DeveloperButtons';
import { SiteLogoButton } from './SiteLogoButton';
import { HelpButton } from './HelpButton';
import { NotificationsButton } from './NotificationsButton';
import { SettingsButton } from './SettingsButton';
import { AccountButton } from './AccountButton';
import { LoginButton } from './LoginButton';
import { CreateComponent } from './CreateComponent';
import { SearchBar } from './SearchBar';
import { RovoButton } from './RovoButton';

export const TopNavigation = ({ defaultCollapsed }) => {
	const { data, loading, error } = useQuery<QueryData>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		TopNavigationQuery,
		{
			variables: {
				includeNav4AdminOptIn: fg('confluence_nav4_ga_phase_1'),
			},
		},
	);

	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	if (loading) {
		return null;
	}

	return (
		<>
			<TopNavigationInner data={data} defaultCollapsed={defaultCollapsed} />
			{error && <ErrorDisplay error={error} />}
		</>
	);
};

type TopNavigationInnerProps = {
	data: QueryData | undefined;
	defaultCollapsed: boolean;
};

const isAtlassianStaff = (data?: QueryData) => {
	return (
		data?.user?.emails?.some(
			(email) =>
				email?.value &&
				(email.value.endsWith('@atlassian.com') ||
					email.value.endsWith('@atlassian.com.invalid') ||
					email.value.endsWith('@usersinbuckets.com')),
		) || false
	);
};

const TopNavigationInner = ({ data, defaultCollapsed }: TopNavigationInnerProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isLoggedIn } = useSessionData();
	const { formatMessage } = useIntl();
	const isOnEditRoute = useIsEditorPage();
	const isAtlassianStaffMemoized = useMemo(() => isAtlassianStaff(data), [data]);
	const getShortcutVisualizer = useCallback(
		(label: string) => (
			<ShortcutVisualizer
				contentBefore={label}
				shortcut={UNIVERSAL_SIDE_NAVIGATION_SHORTCUT}
				isEditorShortcut={isOnEditRoute}
				isTooltip
			/>
		),
		[isOnEditRoute],
	);
	const [{ isMultiSelectActive }, { exitMultiSelect }] = useContentTreeSelection();

	const handleMultiSelectOnCollapse = useCallback(() => {
		if (isMultiSelectActive) {
			exitMultiSelect();
			experienceTracker.abort({
				name: BULK_ACTIONS_IN_CONTENT_TREE_EXPERIENCE,
				reason: 'collapsed side nav',
			});
		}
	}, [isMultiSelectActive, exitMultiSelect, experienceTracker]);

	return (
		<>
			<TopNavStart>
				<SpotlightTarget name="nav4-spotlight-1">
					<SideNavToggleButton
						defaultCollapsed={defaultCollapsed}
						collapseLabel={getShortcutVisualizer(
							formatMessage(i18n.sideNavToggleButtonCollapseLabel),
						)}
						expandLabel={getShortcutVisualizer(formatMessage(i18n.sideNavToggleButtonExpandLabel))}
						onClick={handleMultiSelectOnCollapse}
					/>
				</SpotlightTarget>
				<AppSwitcherButton />
				<DeveloperButtons isAtlassianStaff={isAtlassianStaffMemoized} />
				<SiteLogoButton
					siteConfiguration={data?.siteConfiguration}
					siteSettings={data?.siteSettings}
					siteDescription={data?.siteDescription}
				/>
				{data?.siteSettings?.showApplicationTitle ? (
					<CustomTitle>{data?.siteSettings.siteTitle}</CustomTitle>
				) : null}
			</TopNavStart>
			<TopNavMiddle>
				<SearchBar />
				<CreateComponent />
			</TopNavMiddle>
			<TopNavEnd>
				<EditionAwareness />
				{fg('edition_awareness_standard_upsell_confluence_ab_fg') && (
					<EditionAwarenessUpsellExperiment navVersion="nav4" />
				)}
				<RovoButton />
				<NotificationsButton />
				<HelpButton />
				<SettingsButton />
				{isLoggedIn && data?.user ? (
					<SpotlightTarget name="nav-profile">
						<AccountButton user={data.user} />
					</SpotlightTarget>
				) : (
					<LoginButton />
				)}
			</TopNavEnd>
			<ExperienceSuccess name={APP_NAV_GLOBAL_EXPERIENCE} attributes={{ navVersion: '4' }} />
			<PageSegmentLoadEnd
				metric={APP_NAVIGATION_METRIC}
				customData={{
					isAnonymous: !isLoggedIn,
				}}
			/>
			<PerformanceEnd
				subject={PERFORMANCE_SUBJECT_navigation}
				subjectId="NavigationLoading"
				includeFeatureFlags
				details={nav4Details}
			/>
			<PerformanceEnd
				subject={PERFORMANCE_SUBJECT_navigationFMP}
				subjectId="NavigationFMP"
				includeFeatureFlags
				details={nav4Details}
			/>
		</>
	);
};

const i18n = defineMessages({
	sideNavToggleButtonCollapseLabel: {
		id: 'app-navigation.top-navigation.side-nav-toggle-button-collapse-label',
		defaultMessage: 'Collapse sidebar',
		description:
			'Label to use for side nav toggle button when it will collapse the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
	},
	sideNavToggleButtonExpandLabel: {
		id: 'app-navigation.top-navigation.side-nav-toggle-button-expand-label',
		defaultMessage: 'Expand sidebar',
		description:
			'Label to use for side nav toggle button when it will expand the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
	},
});

const nav4Details = { navVersion: '4' };
